/**
 * Ultra pürüzsüz kaydırma sistemi
 * Lenis kütüphanesi kullanılarak geliştirilmiş profesyonel seviye kaydırma deneyimi
 * Fizik tabanlı, inertia destekli animasyonlar ile doğal ve akıcı hareket sağlar
 */

import Lenis from 'lenis';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

// GSAP entegrasyonu için ScrollTrigger'ı kaydet
if (typeof window !== 'undefined') {
  gsap.registerPlugin(ScrollTrigger);
}

// Ana Lenis instance
let lenis = null;

/**
 * Lenis smooth scroll sistemini başlat
 * @param {Object} options - Lenis konfigürasyon seçenekleri
 */
export const initSmoothScrolling = (options = {}) => {
  if (typeof window === 'undefined') return;
  
  // Halihazırda bir Lenis instance varsa yok et
  if (lenis) {
    lenis.destroy();
  }
  
  // Varsayılan seçenekler
  const defaultOptions = {
    duration: 1.2,
    easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)), // Exponential ease out
    direction: 'vertical',
    gestureDirection: 'vertical',
    smooth: true,
    mouseMultiplier: 1,
    smoothTouch: false,
    touchMultiplier: 2,
    infinite: false,
    autoResize: true,
    orientation: 'vertical',
    // İç içe scroll için gerekli ayarlar
    wheelEventsTarget: window, // Wheel olayları için hedef
    syncTouch: false, // Touch olayları için senkronizasyon devre dışı
    syncTouchLerp: 0.1, // Touch senkronizasyonu için lerp değeri
  };
  
  // Seçenekleri birleştir
  const lenisOptions = { ...defaultOptions, ...options };
  
  // Lenis instance oluştur
  lenis = new Lenis(lenisOptions);

  // İç içe kaydırma için özel ele alıcı ekleme
  const scrollableElements = document.querySelectorAll('.scrollable, [data-lenis-prevent], [data-scroll-container]');
  scrollableElements.forEach(element => {
    element.setAttribute('data-lenis-prevent', '');
  });
  
  // RAF (RequestAnimationFrame) ile Lenis'i güncelle
  const raf = (time) => {
    lenis.raf(time);
    requestAnimationFrame(raf);
  };
  
  // GSAP ScrollTrigger entegrasyonu
  if (ScrollTrigger) {
    lenis.on('scroll', ScrollTrigger.update);
    
    // GSAP ile senkronize et
    gsap.ticker.add((time) => {
      lenis.raf(time * 1000);
    });
  } else {
    // Normal RAF kullan
    requestAnimationFrame(raf);
  }
  
  // Özel scroll olayını ekle
  lenis.on('scroll', ({ scroll, limit, velocity, direction, progress }) => {
    window.dispatchEvent(new CustomEvent('smoothScroll', { 
      detail: {
        position: scroll,
        limit,
        velocity,
        direction,
        progress,
        instance: lenis
      }
    }));
  });
  
  // Sayfa içi linkleri için smooth scroll
  initSmoothScrollLinks();
  
  // Sayfayı başlatırken scroll pozisyonunu sıfırla
  if (window.history.scrollRestoration) {
    window.history.scrollRestoration = 'manual';
  }
  
  // Mobil deneyim optimizasyonu
  function isTouchDevice() {
    return 'ontouchstart' in window || navigator.maxTouchPoints > 0;
  }
  
  // Wheel olayını yöneten yardımcı fonksiyon
  const handleWheel = (e) => {
    // Alt tuşuna basılıyken smooth scroll devre dışı kalsın
    if (e.altKey) {
      lenis.stop();
      setTimeout(() => lenis.start(), 50);
    }
  };
  
  window.addEventListener('wheel', handleWheel, { passive: false });
  
  if (isTouchDevice()) {
    // Mobil cihazlarda alternatif ayarlar uygula
    lenis.options.smoothTouch = true;
    lenis.options.touchMultiplier = 1.5;
  }
  
  return lenis;
};

/**
 * Sayfa içi linkleri için yumuşak kaydırma
 * @param {Object} options - Kaydırma seçenekleri
 */
export const initSmoothScrollLinks = (options = {}) => {
  if (typeof document === 'undefined') return;
  
  // Tüm iç linkleri bul
  document.querySelectorAll('a[href^="#"]').forEach(anchor => {
    anchor.addEventListener('click', function(e) {
      e.preventDefault();
      
      const targetId = this.getAttribute('href');
      if (targetId === '#') {
        // Sayfa başına smooth scroll
        scrollToTop({
          ...options,
          onComplete: () => {
            if (options.updateURL) {
              history.pushState(null, null, window.location.pathname + window.location.search);
            }
          }
        });
      } else {
        // Belirli bir elemana smooth scroll
        const targetElement = document.querySelector(targetId);
        if (targetElement) {
          smoothScrollTo(targetElement, {
            ...options,
            onComplete: () => {
              if (options.updateURL) {
                history.pushState(null, null, window.location.pathname + window.location.search + targetId);
              }
              
              // Odaklanma (erişilebilirlik için)
              if (targetElement.tabIndex === -1) {
                targetElement.setAttribute('tabindex', '-1');
                targetElement.focus({ preventScroll: true });
              }
            }
          });
        }
      }
    });
  });
};

/**
 * Hedef elemana veya pozisyona yumuşak kaydırma
 * @param {HTMLElement|number|string} target - Hedef element, y pozisyonu veya seçici
 * @param {Object} options - Kaydırma seçenekleri
 */
export const smoothScrollTo = (target, options = {}) => {
  if (!lenis) {
    console.warn('Lenis henüz başlatılmamış! smoothScrollTo çalışmayacak.');
    return;
  }
  
  const { 
    offset = 0,
    duration = 1.2,
    easing = (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)), // Varsayılan Lenis easing
    immediate = false,
    lock = false,
    onComplete = null
  } = options;
  
  let targetPosition;
  
  // Hedefi kontrol et
  if (typeof target === 'number') {
    // Doğrudan pozisyon
    targetPosition = target;
  } else if (typeof target === 'string') {
    // CSS seçici
    const element = document.querySelector(target);
    if (!element) {
      console.warn(`Hedef element bulunamadı: ${target}`);
      return;
    }
    
    lenis.scrollTo(element, { 
      offset,
      duration,
      easing,
      immediate,
      lock,
      onComplete
    });
    return;
  } else if (target instanceof HTMLElement) {
    // DOM Element
    lenis.scrollTo(target, { 
      offset,
      duration,
      easing,
      immediate,
      lock,
      onComplete
    });
    return;
  } else {
    console.warn('Geçersiz hedef tipi. Number, String veya HTMLElement olmalı.');
    return;
  }
  
  // Sayısal pozisyona kaydır
  lenis.scrollTo(targetPosition, { 
    offset,
    duration,
    easing,
    immediate,
    lock,
    onComplete
  });
};

/**
 * Sayfa başına yumuşak kaydırma
 * @param {Object} options - Kaydırma seçenekleri
 */
export const scrollToTop = (options = {}) => {
  if (!lenis) {
    console.warn('Lenis henüz başlatılmamış! scrollToTop çalışmayacak.');
    return;
  }
  
  lenis.scrollTo(0, options);
};

/**
 * Element görünür alanda değilse kaydır
 * @param {HTMLElement|string} element - Hedef element veya seçici
 * @param {Object} options - Kaydırma seçenekleri
 */
export const scrollIntoViewIfNeeded = (element, options = {}) => {
  if (!lenis) {
    console.warn('Lenis henüz başlatılmamış! scrollIntoViewIfNeeded çalışmayacak.');
    return;
  }
  
  const targetElement = typeof element === 'string' 
    ? document.querySelector(element) 
    : (element instanceof HTMLElement ? element : null);
    
  if (!targetElement) return;
  
  const rect = targetElement.getBoundingClientRect();
  const windowHeight = window.innerHeight;
  
  // Element zaten görünür alanda mı?
  if (rect.top >= 0 && rect.bottom <= windowHeight) {
    return; // Zaten görünür
  }
  
  // Görünür değilse kaydır
  lenis.scrollTo(targetElement, options);
};

/**
 * Lenis animasyonları duraklat
 */
export const pauseScroll = () => {
  if (lenis) {
    lenis.stop();
  }
};

/**
 * Lenis animasyonları devam ettir
 */
export const resumeScroll = () => {
  if (lenis) {
    lenis.start();
  }
};

/**
 * Kaydırmayı belirli bir süre için kilitle
 * @param {number} duration - Kilitleme süresi (ms)
 */
export const lockScrollTemporarily = (duration = 1000) => {
  if (lenis) {
    lenis.stop();
    setTimeout(() => {
      lenis.start();
    }, duration);
  }
};

/**
 * Mevcut scroll pozisyonunu al
 * @returns {number} Şu anki scroll pozisyonu
 */
export const getCurrentScrollPosition = () => {
  return lenis ? lenis.scroll : (window.scrollY || window.pageYOffset);
};

/**
 * Lenis instance'ı al
 * @returns {Lenis|null} Lenis instance'ı
 */
export const getLenis = () => lenis;

// Dışa aktarılan API
export default {
  initSmoothScrolling,
  smoothScrollTo,
  scrollToTop,
  scrollIntoViewIfNeeded,
  pauseScroll,
  resumeScroll,
  lockScrollTemporarily,
  getLenis,
  getCurrentScrollPosition
}; 